import { useState } from 'react';
import Popover from '@mui/material/Popover';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickersDay, type PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isAfter, isBefore, isSameDay, parseISO, startOfYear, subDays } from 'date-fns';

import s from './DateRangeChip.module.css';

type DateRangeChipProps = {
  /** 'yyyy-MM-dd' or empty string when unset. */
  fromDate: string;
  toDate: string;
  onChange: (fromDate: string, toDate: string) => void;
};

const toYmd = (d: Date) => format(d, 'yyyy-MM-dd');

const PRESETS: { label: string; range: () => [string, string] }[] = [
  { label: 'All time', range: () => ['', ''] },
  { label: 'Last 30 days', range: () => [toYmd(subDays(new Date(), 30)), toYmd(new Date())] },
  { label: 'Last 90 days', range: () => [toYmd(subDays(new Date(), 90)), toYmd(new Date())] },
  { label: 'This year', range: () => [toYmd(startOfYear(new Date())), toYmd(new Date())] },
];

function getPresetLabel(fromDate: string, toDate: string): string | null {
  const match = PRESETS.find((preset) => {
    const [f, t] = preset.range();
    return f === fromDate && t === toDate;
  });
  return match ? match.label : null;
}

function formatChipLabel(from: Date | null, to: Date | null): string {
  if (from && to) {
    return from.getFullYear() === to.getFullYear()
      ? `${format(from, 'MMM d')} – ${format(to, 'MMM d, yyyy')}`
      : `${format(from, 'MMM d, yyyy')} – ${format(to, 'MMM d, yyyy')}`;
  }
  if (from) return `From ${format(from, 'MMM d, yyyy')}`;
  if (to) return `Until ${format(to, 'MMM d, yyyy')}`;
  return 'All time';
}

type RangeDayProps = PickersDayProps & {
  rangeFrom?: Date | null;
  rangeTo?: Date | null;
};

/** Calendar day that highlights the picked range on top of the stock PickersDay. */
function RangeDay({ rangeFrom, rangeTo, day, ...other }: RangeDayProps) {
  const isEdge = (rangeFrom && isSameDay(day, rangeFrom)) || (rangeTo && isSameDay(day, rangeTo));
  const inRange = rangeFrom && rangeTo && isAfter(day, rangeFrom) && isBefore(day, rangeTo);

  return (
    <PickersDay
      {...other}
      day={day}
      selected={Boolean(isEdge)}
      sx={inRange ? { bgcolor: 'color-mix(in srgb, currentColor 10%, transparent)' } : undefined}
    />
  );
}

function DateRangeChip({ fromDate, toDate, onChange }: DateRangeChipProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const from = fromDate ? parseISO(fromDate) : null;
  const to = toDate ? parseISO(toDate) : null;
  const isActive = Boolean(from || to);

  const close = () => setAnchorEl(null);

  const handleDaySelect = (date: Date | null) => {
    if (!date) return;
    // First click (or a click before the current start) begins a new range; the second completes it.
    if (!from || to || isBefore(date, from)) {
      onChange(toYmd(date), '');
      return;
    }
    onChange(fromDate, toYmd(date));
    close();
  };

  return (
    <>
      <button
        type="button"
        className={`${s.chip} ${isActive ? s.chipActive : ''}`}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="dialog"
        aria-expanded={open}>
        <CalendarTodayIcon sx={{ fontSize: 14 }} />
        {getPresetLabel(fromDate, toDate) ?? formatChipLabel(from, to)}
      </button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: '12px',
              boxShadow: '0 12px 32px rgba(15, 23, 42, 0.14), 0 2px 6px rgba(15, 23, 42, 0.06)',
            },
          },
        }}>
        <div className={s.popover}>
          <div className={s.presets}>
            {PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                className={s.preset}
                onClick={() => {
                  const [f, t] = preset.range();
                  onChange(f, t);
                  close();
                }}>
                {preset.label}
              </button>
            ))}
          </div>

          <div className={s.hint}>
            <span>{!from || to ? 'Pick a start date' : 'Pick an end date'}</span>
            {isActive && (
              <button type="button" className={s.clear} onClick={() => onChange('', '')}>
                <CloseIcon sx={{ fontSize: 12 }} />
                Clear
              </button>
            )}
          </div>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateCalendar
              value={to ?? from}
              onChange={handleDaySelect}
              disableFuture
              showDaysOutsideCurrentMonth
              slots={{ day: RangeDay }}
              // slotProps.day doesn't model custom props; RangeDay consumes rangeFrom/rangeTo.
              slotProps={{ day: { rangeFrom: from, rangeTo: to } as Partial<RangeDayProps> }}
              sx={{ width: 296 }}
            />
          </LocalizationProvider>
        </div>
      </Popover>
    </>
  );
}

export default DateRangeChip;
